/**
 * 원본 SQL · 액세스 패턴 — 테이블 분석 창 **오른쪽**에 열린다(2026-10-06 사용자 요구: 「수집한 근거의 SQL 00건을 누르면 원본 쿼리와
 * 액세스 패턴이 오른쪽에 열거」).
 *
 * 테이블의 「SQL n건」 을 누르면 그 테이블의 SQL 전부, 조건 조합의 「SQL n건」 을 누르면 **그 조합의 SQL 만**(서버가 집계와 같은 함수로
 * 붙인 `combo` 로 거른다 — 화면에서 따로 만들면 눌러도 0건이 된다).
 *
 * 원본에는 리터럴(실제 값)이 들어 있다 — 서버가 **주인에게만** 준다. 이 화면은 보여 주기만 하고 AI 로 보내는 길이 없다
 * (AI 는 요약만 쓴다 — backend tableAnalysis/rawStore.js). 저장 전이면 collectId 로 서버의 임시 보관분을 본다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiLoader, FiX } from 'react-icons/fi';
import { getTableRaw } from '../../services/assistantClient';
import { formatApiError } from '../../utils/formatters';

/** 내부 표기 대신 뜻으로 — INTERNAL_FUNCTION = 암시적 형변환, ARITH = 컬럼에 붙은 산술(`col + 0` 등). 둘 다 인덱스를 못 탄다 */
const SPECIAL_FN = { INTERNAL_FUNCTION: 'assistant:tableAnalysis.implicitConv', ARITH: 'assistant:tableAnalysis.arithExpr' };

/** 조건 하나 */
function Term({ c, t }) {
    const name = c.fn && !SPECIAL_FN[c.fn] ? `${c.fn}(${c.column})` : c.column;
    return (
        <span className="ta-sideTerm">
            <span className={`ta-sideSrc ta-sideSrc-${c.source}`}>{c.source === 'access' ? 'access' : 'filter'}</span>
            <b>{name}</b> {c.op}
            {c.rhs === 'column' ? <span className="ta-meta">{t('assistant:tableAnalysis.joinTerm')}</span> : null}
            {SPECIAL_FN[c.fn] ? <span className="tcf-chipModel">{t(SPECIAL_FN[c.fn])}</span> : null}
        </span>
    );
}
Term.propTypes = { c: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

function TableRawPanel({ connectionId, owner, table, collectId, combo, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [openPred, setOpenPred] = useState({});      // sqlId#planHash → 조건 원문 펼침

    useEffect(() => {
        let alive = true;
        setData(null); setError('');
        getTableRaw({ connectionId, owner, table, collectId })
            .then((r) => { if (alive) setData(r); })
            .catch((err) => { if (alive) setError(formatApiError(err, t)); });
        return () => { alive = false; };
    }, [connectionId, owner, table, collectId, t]);

    // 조합을 눌렀으면 그 조합의 SQL 만. null = 전부, '' = 「조건 없음」 조합
    const sqls = useMemo(() => {
        const all = (data && data.sqls) || [];
        return combo === null || combo === undefined ? all : all.filter(s => (s.combo || '') === combo);
    }, [data, combo]);

    return (
        <aside className="ta-side" aria-label={t('assistant:tableAnalysis.sideTitle')}>
            <div className="ta-sideHead">
                <div>
                    <div className="ta-sideTitle">{owner}.{table}</div>
                    {/* 건수는 받은 뒤에만 — 못 받았을 때 「0건」 이라고 쓰면 그 조합에 SQL 이 없는 것처럼 읽힌다 */}
                    {data ? (
                        <div className="tcf-hint">
                            {combo === null || combo === undefined
                                ? t('assistant:tableAnalysis.sideAll', { count: sqls.length })
                                : t('assistant:tableAnalysis.sideCombo', { combo: combo || t('assistant:tableAnalysis.noPredicate'), count: sqls.length })}
                        </div>
                    ) : null}
                </div>
                <button type="button" className="tcf-iconBtn" title={t('common:ui.close')} onClick={onClose}><FiX size={13} /></button>
            </div>
            {data ? (
                <div className="tcf-hint ta-sideNote">
                    {t(data.saved ? 'assistant:tableAnalysis.rawSaved' : 'assistant:tableAnalysis.rawUnsaved', { count: (data.sqls || []).length })}
                    {' '}{t('assistant:tableAnalysis.rawPrivate')}
                </div>
            ) : null}
            {error ? <div className="gm-dropped">{error}</div> : null}
            {!data && !error ? <div className="tcf-hint"><FiLoader size={11} className="tcf-spin" /></div> : null}
            {data && !sqls.length ? <div className="tcf-hint">{t('assistant:tableAnalysis.rawNoSql')}</div> : null}
            <div className="ta-sideList">
                {sqls.map((s) => {
                    const k = `${s.sqlId}#${s.planHash || ''}`;   // 서버가 (SQL, 계획)을 하나로 합쳐 주므로 겹치지 않는다
                    const sum = s.summary || { access: [], columns: [] };
                    const preds = (s.planRows || []).filter(p => p.accessPredicates || p.filterPredicates);
                    return (
                        <div className="ta-sideSql" key={k}>
                            <div className="ta-sideSqlHead">
                                <b>{s.sqlId}</b>
                                {s.seenCount ? <span className="ta-meta">{t('assistant:tableAnalysis.rawSeen', { count: s.seenCount })}</span> : null}
                            </div>
                            {/* 액세스 패턴 — 이 테이블을 어떻게 읽었나(접근 방식) · 어떤 조건이 액세스/필터로 걸렸나 */}
                            <div className="ta-sidePattern">
                                <span className="tcf-label">{t('assistant:tableAnalysis.sidePattern')}</span>
                                {(sum.access || []).map(m => <span key={m} className="tcf-chipModel">{m}</span>)}
                                {(sum.columns || []).length
                                    ? sum.columns.map((c, i) => <Term key={`${c.column}${c.op}${c.source}${i}`} c={c} t={t} />)
                                    : <i className="ta-meta">{t('assistant:tableAnalysis.noPredicate')}</i>}
                            </div>
                            <pre className="ta-ddl ta-sideText">{s.sqlText || t('assistant:tableAnalysis.rawNoText')}</pre>
                            {preds.length ? (
                                <>
                                    <button type="button" className="tcf-linkBtn" onClick={() => setOpenPred(o => ({ ...o, [k]: !o[k] }))}>
                                        {t(openPred[k] ? 'assistant:tableAnalysis.sidePredHide' : 'assistant:tableAnalysis.sidePredShow', { count: preds.length })}
                                    </button>
                                    {openPred[k] ? (
                                        <table className="ta-rawPlan">
                                            <tbody>
                                                {preds.map(p => (
                                                    <tr key={p.id}>
                                                        <td className="ta-meta">{p.id}</td>
                                                        <td>{`${p.operation || ''} ${p.options || ''}`.trim()}{p.objectName ? ` · ${p.objectName}` : ''}</td>
                                                        <td>
                                                            {p.accessPredicates ? <div><span className="ta-meta">access</span> {p.accessPredicates}</div> : null}
                                                            {p.filterPredicates ? <div><span className="ta-meta">filter</span> {p.filterPredicates}</div> : null}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
}

TableRawPanel.propTypes = {
    connectionId: PropTypes.string.isRequired,
    owner: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    collectId: PropTypes.string,
    combo: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};
TableRawPanel.defaultProps = { collectId: '', combo: null };

export default TableRawPanel;
